/**
 * /reserva-starlink-mini/exito — Post-Stripe success. Polls the reservation
 * status until it becomes `reservado` (paid), or up to ~30 seconds.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { CheckCircle2, Loader2, PackageCheck, Mail, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

export default function StarlinkExitoPage() {
  const { setLanguage } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null, tries: 0 });

  useEffect(() => { setLanguage("es"); document.title = "Reserva confirmada · Starlink Mini | GTX"; }, [setLanguage]);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", data: null, tries: 0 }); return; }
    let stop = false;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/starlink/reservations/status/${sessionId}`);
        tries += 1;
        if (stop) return;
        if (r.data.status === "reservado" || r.data.status === "pedido_cursado" || r.data.status === "saldo_pagado" || r.data.status === "enviado") {
          setState({ status: "paid", data: r.data, tries });
        } else if (tries >= 15) {
          setState({ status: "timeout", data: r.data, tries });
        } else {
          setState({ status: "polling", data: r.data, tries });
          setTimeout(poll, 2000);
        }
      } catch (e) {
        if (!stop) setState({ status: "error", data: null, tries });
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white noise flex items-center justify-center px-5 py-16" data-testid="starlink-exito-page">
      <Helmet><meta name="robots" content="noindex" /></Helmet>

      <div className="w-full max-w-lg">
        {state.status === "paid" && (
          <div className="rounded-2xl border border-emerald-500/40 bg-[#0F0F16] p-8 lg:p-10 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-300" />
            </div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-emerald-400 mono mb-2">Reserva confirmada</div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight mb-3">¡Estás dentro del lote!</h1>
            <p className="text-white/70 mb-6">Tu Starlink Mini ya tiene sitio. Te enviaremos actualizaciones por email a <b>{state.data?.email}</b>.</p>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-left space-y-3 mb-6" data-testid="exito-recap">
              <Row k="Nº de pedido" v={state.data?.order_number} big />
              <Row k="Reserva pagada" v={fmtEUR(state.data?.importe_reserva || 0)} />
              <Row k="Saldo antes del envío" v={fmtEUR(state.data?.importe_saldo || 0)} />
              <Row k="Total del kit" v={fmtEUR(state.data?.precio_total || 0)} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/mi-reserva" className="flex-1">
                <button className="w-full bg-blue-500 hover:bg-blue-400 text-white rounded-lg px-5 py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
                  <PackageCheck className="w-4 h-4" />
                  Seguir mi reserva
                </button>
              </Link>
              <Link to="/" className="flex-1">
                <button className="w-full border border-white/15 hover:bg-white/5 text-white/80 rounded-lg px-5 py-3 text-sm font-semibold">Volver al inicio</button>
              </Link>
            </div>

            <p className="mt-6 text-xs text-white/40 flex items-center justify-center gap-2">
              <Mail className="w-3.5 h-3.5" /> Guarda tu nº de pedido — lo necesitas para consultar el estado.
            </p>
          </div>
        )}

        {state.status === "polling" || state.status === "loading" ? (
          <div className="rounded-2xl border border-white/10 bg-[#0F0F16] p-10 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-4" />
            <div className="font-bold mb-1">Confirmando tu pago…</div>
            <p className="text-white/50 text-sm">Un momento — estamos hablando con Stripe.</p>
          </div>
        ) : null}

        {state.status === "timeout" && (
          <div className="rounded-2xl border border-yellow-500/40 bg-[#0F0F16] p-8 text-center">
            <div className="font-bold mb-2">Estamos verificando tu pago</div>
            <p className="text-white/60 text-sm mb-4">Stripe está tardando más de lo normal. Recibirás un email en cuanto confirmemos. También puedes consultar el estado de tu reserva.</p>
            <Link to="/mi-reserva" className="inline-flex items-center gap-2 text-blue-300 text-sm underline">
              Consultar mi reserva <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {state.status === "error" && (
          <div className="rounded-2xl border border-red-500/40 bg-[#0F0F16] p-8 text-center">
            <div className="font-bold mb-2">No se ha podido verificar el pago</div>
            <p className="text-white/60 text-sm mb-4">Escríbenos y lo resolvemos rápido: <a className="text-blue-300 underline" href="mailto:adm@globaltechxperts.nl">adm@globaltechxperts.nl</a>.</p>
            <Link to="/reserva-starlink-mini" className="text-blue-300 text-sm underline">Volver a la campaña</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, big = false }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/50">{k}</span>
      <span className={big ? "font-black text-lg text-blue-300 tracking-wider mono" : "font-semibold"}>{v}</span>
    </div>
  );
}
